'use client';

import { useRef } from 'react';

import { cn } from '@/lib/utils';

import { getLinkRelFromConfig } from '@/lib/link-config';

import { ServiceStatusProps } from '@/components/atoms/Instatus/index';
import { ServiceStatusIcon } from '@/components/atoms/Instatus/StatusIcon';

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { TypographyP } from '@/components/ui/typography';
import { Spinner } from '@/components/atoms/spinner';
import { env } from '@/constant/process';

const DECODO_INSTATUS =
  env.publicLocale === 'zh-cn'
    ? 'https://status.decodo.com/zh'
    : 'https://status.decodo.com';

export const ServiceStatusComponent = ({
  message,
  classNames,
  size,
  status,
}: {
  message: string;
  status: 'success' | 'error' | 'loading';
  classNames?: ServiceStatusProps['classNames'];
  size?: ServiceStatusProps['size'];
}) => {
  const textContainerRef = useRef<HTMLDivElement>(null);

  const maxTextWidth: number | undefined = textContainerRef?.current
    ?.clientWidth
    ? textContainerRef?.current?.clientWidth - 8
    : undefined;

  return (
    <TooltipProvider delayDuration={50}>
      <Tooltip>
        <TooltipTrigger asChild>
          <a
            href={DECODO_INSTATUS}
            target='_blank'
            rel={getLinkRelFromConfig(DECODO_INSTATUS)}
            data-tracker-type='link'
            data-tracker-context='service-status'
            className={cn(
              'flex w-full flex-row items-center overflow-hidden rounded border border-gray-700 dark:border-gray-dark-700',
              size === 'sm' ? 'p-2' : 'p-4',
              classNames?.button,
            )}
          >
            {status === 'loading' ? (
              <Spinner />
            ) : (
              <ServiceStatusIcon status={status} />
            )}
            <div ref={textContainerRef} className='block w-full flex-1'>
              <TypographyP
                className={cn(
                  // NOTE: when editing paddings here, adjust maxTextWidth accordingly
                  'text-button-text-s ml-2 max-w-full overflow-hidden text-ellipsis whitespace-nowrap font-serif text-base-black dark:text-base-dark-white mt-0',
                  size === 'sm' ? 'text-body-s' : 'text-body-m',
                  classNames?.text
                )}
                style={{ maxWidth: maxTextWidth }}
              >
                {message}
              </TypographyP>
            </div>
          </a>
        </TooltipTrigger>
        <TooltipContent 
          className={cn(
            'bg-gray-900 dark:bg-gray-dark-900',
            'border border-gray-700 dark:border-gray-dark-700',
            'px-3 py-1 rounded',
            classNames?.tooltip?.tooltip
          )}
        >
          <TypographyP className={cn(
            'mt-0 text-body-s-bold dark:text-base-dark-white text-base-white',
            classNames?.tooltip?.text
          )}>
            {message}
          </TypographyP>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
